import { ComponentType, CSSProperties, ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useDraggable } from '@dnd-kit/core';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import {
  ApplicantCard,
  ApplicantCardActivity,
  ApplicantCardProfile,
  ApplicantCardTags,
} from '@components/applicant-card';
import { Badge, BadgeProps } from '@components/badge';
import { Button } from '@components/button';
import { IconProps } from '@components/icons';
import { IconAppExtraSmall } from '@components/icons/app-extra-small';
import { IconAssigneeSmall } from '@components/icons/assignee-small';
import { IconErrorSmall } from '@components/icons/error-small';
import { IconFilesSmall } from '@components/icons/files-small';
import { IconMessagesSmall } from '@components/icons/messages-small';
import { IconNotesSmall } from '@components/icons/notes-small';
import { IconRatingSmall } from '@components/icons/rating-small';
import { IconTasklistSmall } from '@components/icons/tasklist-small';
import { Stack } from '@components/stack';
import { abbreviateDate } from '@utils/date';
import { abbreviate } from '@utils/strings';

import { useAddFavorite, useCachedFavorites, useRemoveFavorite } from 'api/favorites/hooks';
import { useStore } from 'models/Provider';
import { IApplication, IApplicationIntegrationsLabels } from 'models/types';

type Props = {
  application: IApplication;
  active?: boolean;
  isOverlay?: boolean;
};

type DraggablePipelineCardProps = {
  application: IApplication;
  stageId?: number;
};

export const DraggablePipelineCard = observer<DraggablePipelineCardProps>(
  function DraggablePipelineCard(props) {
    const { application, stageId } = props;

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: 'a--' + String(application.id),
      data: {
        type: 'card',
        stageId,
      },
    });

    const style: CSSProperties = {
      cursor: 'grab',
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
        <PipelineCard application={application} />
      </div>
    );
  }
);

export const PipelineCard = observer<Props>(function PipelineCard(props) {
  const { lang } = useStore();
  const { application, active } = props;

  const statusLine = application.isHired ? 'green' : application.isRejected ? 'red' : undefined;

  const favorites = useCachedFavorites();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  const favoriteId = favorites.find(
    (fav) => fav.contentType === 'application' && fav.objectId === application.id
  )?.id;
  const isFavorited = Boolean(favoriteId);

  const handleFavoriteClick = () => {
    if (favoriteId) {
      removeFavorite.mutate(favoriteId);
    } else {
      addFavorite.mutate({
        contentType: 'application',
        objectId: application.id,
      });
    }
  };

  return (
    <CardLink to={application.detailUrl} id={`application-${application.id}`}>
      <ApplicantCard
        statusLine={statusLine}
        active={active}
        onOnboardingClick={application.isHired ? application.goToOnboarding : undefined}
        onFavoriteClick={handleFavoriteClick}
        isFavorited={isFavorited}
        key={application.id}
        activity={<CardActivity application={application} />}
        tags={<Tags application={application} />}
        profile={
          <ApplicantCardProfile
            avatar={application.picture ?? undefined}
            name={application.name ?? ''}
            detail={abbreviateDate(application.date, lang)}
            avatarBackground={application.isRejected ? 'red' : undefined}
          />
        }
      />
    </CardLink>
  );
});

const CardLink = styled(Link)({
  display: 'block',
});

const CardActivity = observer<CardActivityProps>(function CardActivity(props) {
  const { t } = useStore();
  const { application } = props;

  function renderTasklistItem() {
    if (
      !((application.taskListItems?.length ?? 0) > 0 && Number(application.taskListItems?.[1]) > 0)
    ) {
      return null;
    }
    const diff = Number(application.taskListItems?.[1]) - Number(application.taskListItems?.[0]);
    return (
      <CardActivityItem
        href={application.onboardingUrl}
        highlight={application.userRead ? !application.userRead.taskList : false}
        icon={IconTasklistSmall}
        value={`${application.taskListItems?.[0]} / ${application.taskListItems?.[1]}`}
        tooltip={
          diff === 1 ? t('pipeline.tooltip.tasklist') : t('pipeline.tooltip.tasklists', diff)
        }
      />
    );
  }

  return (
    <ApplicantCardActivity>
      {application.numReviews > 0 && (
        <CardActivityItem
          href={`${application.baseUrl}/ratings`}
          highlight={application.userRead ? !application.userRead.ratings : false}
          icon={IconRatingSmall}
          value={application.numReviews}
          tooltip={
            application.numReviews === 1
              ? t('pipeline.tooltip.rating')
              : t('pipeline.tooltip.ratings', application.numReviews)
          }
        />
      )}
      {application.job.useMessages && application.numMessages > 0 && (
        <CardActivityItem
          href={`${application.baseUrl}/messages`}
          highlight={application.userRead ? !application.userRead.messages : false}
          icon={IconMessagesSmall}
          hasError={application.userRead ? !application.userRead?.messagesFailed : false}
          value={application.numMessages}
          tooltip={
            application.numMessages === 1
              ? t('pipeline.tooltip.message')
              : t('pipeline.tooltip.messages', application.numMessages)
          }
        />
      )}
      {application.numNotes > 0 && (
        <CardActivityItem
          href={`${application.baseUrl}/notes`}
          highlight={application.userRead ? !application.userRead.notes : false}
          icon={IconNotesSmall}
          value={application.numNotes}
          tooltip={
            application.numNotes === 1
              ? t('pipeline.tooltip.note')
              : t('pipeline.tooltip.notes', application.numNotes)
          }
        />
      )}
      {application.numAttachments > 0 && (
        <CardActivityItem
          href={`${application.baseUrl}/files`}
          highlight={application.userRead ? !application.userRead.files : false}
          icon={IconFilesSmall}
          value={application.numAttachments}
          tooltip={
            application.numAttachments === 1
              ? t('pipeline.tooltip.file')
              : t('pipeline.tooltip.files', application.numAttachments)
          }
        />
      )}
      {application.assignedTo && (
        <CardActivityItem
          href={application.detailUrl}
          highlight={application.userRead ? !application.userRead.assignedTo : false}
          icon={IconAssigneeSmall}
          value={abbreviate(application.assignedTo.name)}
          tooltip={t('pipeline.tooltip.assignee', application.assignedTo.name)}
        />
      )}
      {renderTasklistItem()}
    </ApplicantCardActivity>
  );
});

const Tags = observer<TagsProps>(function Tags(props) {
  const { application } = props;
  const { t } = useStore();
  const isNew = application.userRead && !application.userRead.newApplication;

  const tags = [
    application.referredBy && (
      <Badge key="sourced" variant="blue" text={t('application.label.sourced')} />
    ),
    application.isNotifiedOfRejection && (
      <Badge key="rejectionsent" variant="red" text={t('application.label.rejectionsent')} />
    ),
    isNew && <Badge key="new" variant="green" text={t('application.label.new')} />,
    application.isRetracted && (
      <Badge key="retracted" variant="orange" text={t('application.label.retracted')} />
    ),
    application.isManuallyAdded && (
      <Badge key="manuallyadded" text={t('application.label.manuallyadded')} />
    ),
    ...((application.integrationsLabels as IApplicationIntegrationsLabels)?.map((label) => (
      <Badge
        key={label.text}
        variant={label.variant}
        icon={<IconAppExtraSmall />}
        text={label.text}
      />
    )) || []),
  ].filter(Boolean) as ReactElement<BadgeProps>[];

  return <ApplicantCardTags>{tags}</ApplicantCardTags>;
});

type CardActivityItemProps = {
  icon: ComponentType<IconProps>;
  value: string | number;
  highlight: boolean;
  href: string;
  tooltip: string;
  hasError?: boolean;
};

const CardActivityItem = observer<CardActivityItemProps>(function CardActivityItem(props) {
  const navigate = useNavigate();
  const { icon: Icon, value, highlight, href, tooltip, hasError = false } = props;

  return (
    <Button
      size="xs"
      preventDefault
      stopPropagation
      onClick={() => navigate(href)}
      variant="secondary"
      tooltip={tooltip}
    >
      <Stack orientation="horizontal" space={1} align="center">
        <Icon size={16} />
        {hasError && <IconErrorSmall color="error" />}
        <Badge variant={hasError ? 'red' : highlight ? 'green' : 'neutral'} text={value} />
      </Stack>
    </Button>
  );
});

type CardActivityProps = {
  application: IApplication;
};

type TagsProps = {
  application: IApplication;
};
